import { coerceDate } from '../dateLabels';
import { type BookingStatus, type SourceStatus, isUsable } from './types';

/**
 * "Your quarter" — where the member is in the quarterly review cycle.
 *
 * The honest constraint behind this file: **the member cannot see the report
 * pipeline.** A `quarterly_reports` document is unreadable to them until its
 * status is `released`, and `care_workflows` — which holds the real stage and
 * the coordinator's report nudge — is staff-only with no member projection. So
 * every step here is INFERRED from evidence the member is allowed to read:
 * their own intake readiness, their own observations, their own released plan,
 * their own released reports.
 *
 * That is why `expectedReportLabel` is null. Guessing a date the practice has
 * not committed to would be worse than saying a clinician will confirm it. When
 * a `reportCycle` projection exists (see the P1 note in the plan), pass it as
 * `override` and it supersedes the inference.
 *
 * A step whose evidence FAILED to load is `'unknown'`, not `'upcoming'` — a
 * denied read must not look like work the member still has to do.
 */

export type QuarterStepId = 'baseline' | 'bloods' | 'data' | 'clinician_review' | 'report';
export type QuarterStepState = 'done' | 'active' | 'upcoming' | 'unknown';

export interface QuarterStep {
  id: QuarterStepId;
  label: string;
  state: QuarterStepState;
  detail: string;
}

export interface QuarterProgress {
  quarterLabel: string;
  steps: QuarterStep[];
  expectedReportLabel: string | null;
}

export interface QuarterStepperInput {
  now?: Date;
  readiness: { completionRatio: number; ready: boolean } | null;
  intakeStatus: SourceStatus;
  bookingStatus: BookingStatus;
  bookingSourceStatus: SourceStatus;
  /** When the member's most recent lab observation was taken. */
  latestLabObservationAt: Date | null;
  labsStatus: SourceStatus;
  hasOpenBloodPanel: boolean;
  scheduledTestsStatus: SourceStatus;
  latestWearableObservationAt: Date | null;
  wearableStatus: SourceStatus;
  dailyEntriesThisQuarter: number;
  planReleasedAt: Date | null;
  planStatus: SourceStatus;
  releasedReportQuarterLabels: string[];
  reportsStatus: SourceStatus;
  reviewAnchorAt: Date | null;
  /**
   * From `patient_record_projections.reportCycle` — what the practice actually
   * knows, as opposed to what the steps above infer.
   *
   * There is no `stage` here on purpose. `care_workflows.workflowStage` only
   * tracks the intake phase (`new`, `interview_pending`, `interview_complete`,
   * `risk_engine_ready`) and says nothing about the quarterly cycle, so mapping
   * it onto these five steps would be inventing a correspondence that does not
   * exist. `reportStatus` is the field that genuinely describes the report.
   */
  reportCycle?: { reportStatus?: string | null; deliveryCallAt?: Date | null } | null;
}

/** Report statuses that mean written-but-not-yet-with-the-member. */
const IN_PROGRESS_REPORT_STATUSES = new Set([
  'drafted',
  'draft',
  'care_clinician_review',
  'medical_director_signoff',
]);

function reportStatusDetail(status: string): string {
  switch (status) {
    case 'care_clinician_review':
    case 'medical_director_signoff':
      return 'With your clinician for review';
    case 'drafted':
    case 'draft':
      return 'Being written';
    default:
      return 'In progress';
  }
}

const RECENT_WEARABLE_DAYS = 14;
const MIN_DAILY_ENTRIES = 7;

export function currentQuarterLabel(now: Date): string {
  return `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;
}

function after(candidate: Date | null, anchor: Date | null): boolean {
  if (!candidate) return false;
  if (!anchor) return true;
  return candidate.getTime() > anchor.getTime();
}

export function buildQuarterStepper(input: QuarterStepperInput): QuarterProgress {
  const now = input.now ?? new Date();
  const quarterLabel = currentQuarterLabel(now);
  const anchor = input.reviewAnchorAt;

  // ── Baseline ─────────────────────────────────────────────────────────────
  let baseline: QuarterStep;
  if (!isUsable(input.intakeStatus)) {
    baseline = { id: 'baseline', label: 'Baseline', state: 'unknown', detail: 'Not available just now' };
  } else if (input.readiness?.ready) {
    baseline = { id: 'baseline', label: 'Baseline', state: 'done', detail: 'Your history is complete' };
  } else if (input.bookingStatus !== 'none' || (input.readiness?.completionRatio ?? 0) > 0) {
    baseline = {
      id: 'baseline',
      label: 'Baseline',
      state: 'active',
      detail: `${Math.round((input.readiness?.completionRatio ?? 0) * 100)}% captured`,
    };
  } else {
    // Nothing captured and nothing booked. The first step is the history
    // itself, not the appointment — the interview is booked after it.
    baseline = { id: 'baseline', label: 'Baseline', state: 'active', detail: 'Start your health history' };
  }

  // ── Bloods ───────────────────────────────────────────────────────────────
  let bloods: QuarterStep;
  const bloodsDone = after(input.latestLabObservationAt, anchor);
  if (!isUsable(input.labsStatus)) {
    bloods = { id: 'bloods', label: 'Bloods', state: 'unknown', detail: 'Not available just now' };
  } else if (bloodsDone) {
    bloods = { id: 'bloods', label: 'Bloods', state: 'done', detail: 'Results received' };
  } else if (isUsable(input.scheduledTestsStatus) && input.hasOpenBloodPanel) {
    bloods = { id: 'bloods', label: 'Bloods', state: 'active', detail: 'Test booked' };
  } else {
    bloods = { id: 'bloods', label: 'Bloods', state: 'upcoming', detail: 'Not yet scheduled' };
  }

  // ── Data ─────────────────────────────────────────────────────────────────
  let data: QuarterStep;
  const wearableRecent =
    input.latestWearableObservationAt != null &&
    now.getTime() - input.latestWearableObservationAt.getTime() <= RECENT_WEARABLE_DAYS * 24 * 60 * 60 * 1000;
  const enoughEntries = input.dailyEntriesThisQuarter >= MIN_DAILY_ENTRIES;
  if (!isUsable(input.wearableStatus)) {
    data = { id: 'data', label: 'Your data', state: 'unknown', detail: 'Not available just now' };
  } else if (wearableRecent || enoughEntries) {
    data = { id: 'data', label: 'Your data', state: 'done', detail: 'Connected and syncing' };
  } else if (bloods.state === 'done') {
    data = { id: 'data', label: 'Your data', state: 'active', detail: 'Connect a wearable or log a few days' };
  } else {
    data = { id: 'data', label: 'Your data', state: 'upcoming', detail: 'Nothing received yet' };
  }

  // ── Clinician review ─────────────────────────────────────────────────────
  let review: QuarterStep;
  const reviewDone = after(input.planReleasedAt, anchor);
  if (!isUsable(input.planStatus)) {
    review = { id: 'clinician_review', label: 'Clinician review', state: 'unknown', detail: 'Not available just now' };
  } else if (reviewDone) {
    review = { id: 'clinician_review', label: 'Clinician review', state: 'done', detail: 'Your plan has been updated' };
  } else if (bloods.state === 'done') {
    review = { id: 'clinician_review', label: 'Clinician review', state: 'active', detail: 'With your care team' };
  } else {
    review = { id: 'clinician_review', label: 'Clinician review', state: 'upcoming', detail: 'After your results arrive' };
  }

  // ── Report ───────────────────────────────────────────────────────────────
  let report: QuarterStep;
  const reportReleased = input.releasedReportQuarterLabels.includes(quarterLabel);
  if (!isUsable(input.reportsStatus)) {
    report = { id: 'report', label: 'Report', state: 'unknown', detail: 'Not available just now' };
  } else if (reportReleased) {
    report = { id: 'report', label: 'Report', state: 'done', detail: `Your ${quarterLabel} report is ready` };
  } else if (review.state === 'done') {
    report = { id: 'report', label: 'Report', state: 'active', detail: 'Being written' };
  } else {
    report = { id: 'report', label: 'Report', state: 'upcoming', detail: 'After your review' };
  }

  const steps = [baseline, bloods, data, review, report];

  // What the practice knows beats what we inferred — but only for the steps it
  // actually speaks to, and never for one whose own source failed to load.
  const reportStatus = input.reportCycle?.reportStatus || null;
  if (reportStatus && report.state !== 'unknown') {
    if (reportStatus === 'released') {
      report.state = 'done';
      report.detail = `Your ${quarterLabel} report is ready`;
    } else if (IN_PROGRESS_REPORT_STATUSES.has(reportStatus)) {
      report.state = 'active';
      report.detail = reportStatusDetail(reportStatus);
      // A report cannot be in progress without the review that produces it.
      if (review.state === 'upcoming') {
        review.state = 'done';
        review.detail = 'Complete';
      }
    }
  }

  const deliveryCallAt = coerceDate(input.reportCycle?.deliveryCallAt);

  return {
    quarterLabel,
    steps,
    // A booked delivery call, not an estimate. Nothing in the schema records an
    // expected release date, and inventing one would be worse than saying a
    // clinician will confirm — so when there is no call booked, that is exactly
    // what the caller renders.
    expectedReportLabel: deliveryCallAt
      ? deliveryCallAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
      : null,
  };
}
